/**
 * Global State Store
 *
 * 管理全域狀態（認證、頁面轉換、深色模式等）
 */

import { defineStore } from 'pinia'
import mitt, { type Emitter } from 'mitt'
import { authService } from '@shared/services/auth/authService'
import type { App } from 'vue'
import type { Router, RouteLocationNormalized, NavigationFailure } from 'vue-router'

// ==================== 類型定義 ====================

/** 事件總線事件類型 */
export type EventBusEvents = {
  'user:init': unknown
  'user:login': unknown
  'user:logout': void
  'page:changed': {
    path: string
    from: RouteLocationNormalized | null
    to: RouteLocationNormalized | null
    pageName: string
  }
  'field-selected': unknown
  'view:open': {
    path: string
    title: string
    options: Record<string, unknown>
  }
  'dataFieldWithType-updated': void
  [key: string]: unknown
}

/** 抽屜資訊 */
export interface DrawerInfo {
  show: boolean
  name: string
  title: string
  width: number
  parameter: Record<string, unknown>
}

/** Global State 介面 */
export interface GlobalStateType {
  isDark: boolean
  isMobile: boolean
  isTablet: boolean
  pageName: string
  pageTitle: string
  menuTitle: string
  mainMenuName: string
  drawerInfo: DrawerInfo
  isChecked: boolean
  windowWidth: number
  routeFrom: RouteLocationNormalized | null
  routeTo: RouteLocationNormalized | null
  isLogin: boolean
  pendingRoute: string | null
  eventBus?: Emitter<EventBusEvents>
}

// ==================== 計時器管理 ====================

const originalSetInterval = window.setInterval
const originalSetTimeout = window.setTimeout
let intervalTimerList: number[] = []
let timeOutTimerList: number[] = []

// ==================== Event Bus ====================

// 暴露 eventBus 給 Remote 模組使用 (Module Federation)
declare global {
  interface Window {
    __CORE_EVENT_BUS__: Emitter<EventBusEvents>
  }
}

// Module Federation 支援：
// - 如果 __CORE_EVENT_BUS__ 已存在（Remote 被 Host 載入），使用 Host 的實例
// - 否則創建新實例（作為 Host 或獨立運行時）
export const eventBus: Emitter<EventBusEvents> = window.__CORE_EVENT_BUS__ || mitt<EventBusEvents>()

// 設置全局變數（只在 Host 或首次載入時）
if (!window.__CORE_EVENT_BUS__) {
  window.__CORE_EVENT_BUS__ = eventBus
}

// ==================== Store 定義 ====================

export const globalStateData = defineStore('globalStateData', {
  state: (): GlobalStateType => {
    return {
      isDark: localStorage.getItem('app-dark-mode') === 'true',
      isMobile: true,
      isTablet: false,
      pageName: '',
      pageTitle: '',
      menuTitle: '',
      mainMenuName: '',
      drawerInfo: {
        show: false,
        name: '',
        title: '',
        width: 600,
        parameter: {},
      },
      isChecked: false,
      windowWidth: window.innerWidth,
      routeFrom: null,
      routeTo: null,
      isLogin: false,
      pendingRoute: null,
    }
  },

  getters: {},

  actions: {
    /**
     * 初始化認證監聽器
     * 使用 authService 統一管理，避免重複監聽
     */
    async initializeAuthListener(): Promise<unknown> {
      // 使用 authService 初始化
      const currentUser = await authService.initializeAuthListener()

      // 更新本地狀態
      this.isLogin = !!currentUser
      this.isChecked = true

      // 發出初始化事件
      ;(this as unknown as { eventBus: Emitter<EventBusEvents> }).eventBus?.emit('user:init', currentUser)

      // 訂閱後續的認證狀態變化
      authService.onAuthStateChange((event, user) => {
        // 跳過初始事件（已經處理過了）
        if (event === 'INITIAL_SESSION') return

        this.isLogin = !!user
        if (event === 'SIGNED_IN') {
          ;(this as unknown as { eventBus: Emitter<EventBusEvents> }).eventBus?.emit('user:login', user)
        } else if (event === 'SIGNED_OUT') {
          ;(this as unknown as { eventBus: Emitter<EventBusEvents> }).eventBus?.emit('user:logout')
        }
      })

      return currentUser
    },

    /**
     * 處理用戶登出
     */
    async handleUserLogout(): Promise<void> {
      try {
        await authService.signOut()
        this.isLogin = false
        // 清除待處理路由，避免登入後跳到舊頁面
        this.pendingRoute = null
        // 注意：user:logout 事件會由 authService 的 onAuthStateChange 回調觸發
      } catch (error) {
        console.error('登出失敗', error)
      }
    },

    /**
     * 開啟抽屜
     */
    openDrawer(drawerName: string, drawerTitle?: string, drawerParameter?: Record<string, unknown>): void {
      const title = drawerTitle || ''
      const parameter = drawerParameter || {}
      this.drawerInfo.show = true
      this.drawerInfo.title = title
      this.drawerInfo.parameter = parameter
      setTimeout(() => {
        this.drawerInfo.name = drawerName
      }, 330)
    },

    /**
     * 設定頁面定時器
     */
    setPageInterval(callback: () => void, ms: number, keep: boolean = false): void {
      const keepTimer = keep || false
      const i = originalSetInterval(callback, ms)
      if (!keepTimer) intervalTimerList.push(i as unknown as number)
    },

    /**
     * 清除頁面定時器
     */
    clearPageInterval(): void {
      intervalTimerList.forEach(i => {
        clearInterval(i)
      })
      intervalTimerList = []
    },

    /**
     * 設定頁面延時
     */
    setPageTimeout(callback: () => void, ms: number, keep: boolean = false): number {
      const keepTimer = keep || false
      const i = originalSetTimeout(callback, ms)
      if (!keepTimer) timeOutTimerList.push(i as unknown as number)
      return i as unknown as number
    },

    /**
     * 清除頁面延時
     */
    clearPageTimeout(): void {
      timeOutTimerList.forEach(i => {
        clearTimeout(i)
      })
      timeOutTimerList = []
    },

    /**
     * 頁面切換前處理
     */
    beforePageChange(to: RouteLocationNormalized, from: RouteLocationNormalized): boolean | void {
      this.clearPageInterval()
      this.clearPageTimeout()
      this.routeFrom = from
      this.routeTo = to
      if (to.meta && to.meta.requiresAuth) {
        if (!this.isChecked) {
          // Auth 狀態尚未確定，先記錄目標路由
          // 排除登出頁面，避免登入後又跳到登出頁
          if (to.name !== 'general_Logout') {
            this.pendingRoute = to.fullPath
          }
          // 返回 false 取消導航，App.vue 會顯示 QuickAuth
          return false
        } else if (!this.isLogin) {
          // 未登入時取消導航，App.vue 會顯示 QuickAuth
          return false
        }
      }
    },

    /**
     * 檢查是否有待處理的路由
     */
    checkPendingRoute(): string | null {
      if (this.pendingRoute) {
        const route = this.pendingRoute
        this.pendingRoute = null
        return route
      }
      return null
    },

    /**
     * 頁面切換後處理
     */
    afterPageChange(): void {
      const from = this.routeFrom
      const to = this.routeTo
      const path = location.pathname
      let pageName = path.split('/').join('_').substring(1)
      if (pageName.length === 1) pageName = 'components_' + pageName
      this.pageName = to?.meta?.pageName ? (to.meta.pageName as string) : pageName
      setTimeout(() => {
        this.mainMenuName = pageName.split('_')[0]
      }, 30)
      ;(this as unknown as { eventBus: Emitter<EventBusEvents> }).eventBus?.emit('page:changed', { path, from, to, pageName })
    },
  },
})

// ==================== Vue Plugin ====================

export default {
  install: (app: App): void => {
    const gs = globalStateData()
    const router = app.config.globalProperties.$router as Router | undefined
    ;(gs as unknown as { eventBus: Emitter<EventBusEvents> }).eventBus = eventBus

    if (router) {
      // 在背景觸發 auth 初始化（不阻塞路由）
      // App.vue onMounted 的 handleAuthRedirect 也會觸發，authService 有防重入保護
      if (!gs.isChecked) {
        gs.initializeAuthListener()
      }

      router.beforeEach(async (to, from) => {
        if (!gs.isChecked) {
          // 等待 auth 就緒（使用 authService 的 Promise，不會重複觸發初始化）
          await authService.ensureAuthReady()
          if (!gs.isChecked) {
            // ensureAuthReady 完成但 globalState 還沒更新（edge case）
            gs.isChecked = true
            gs.isLogin = !!authService.currentUser
          }
        }
        return gs.beforePageChange(to, from)
      })

      router.afterEach((to, from, failure?: NavigationFailure | void) => {
        if (!failure) {
          // 檢查是否有待處理的路由
          const pendingRoute = gs.checkPendingRoute()
          if (pendingRoute) {
            router.push(pendingRoute)
          } else {
            gs.afterPageChange()
          }
        }
      })
    }

    const view = app.config.globalProperties.view as Record<string, unknown> | undefined
    if (view) {
      view.eventBus = eventBus
      view.globalState = gs
      const vsProxy = new Proxy(view, {
        get(target, key: string) {
          if (key in target) return Reflect.get(target, key)
          return Reflect.get(gs, key)
        },
        set(target, key: string, value) {
          if (key in target) {
            Reflect.set(target, key, value)
            return true
          }
          Reflect.set(gs, key, value)
          return true
        }
      })
      app.config.globalProperties.vs = vsProxy
      app.config.globalProperties.view = vsProxy
    }
    app.config.globalProperties.eventBus = eventBus
    app.config.globalProperties.gs = gs
    app.config.globalProperties.globalState = gs
  }
}
